import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '../ui/button'
import { Textarea } from '../ui/textarea'
import { Label } from '../ui/label'
import {
  StudentDecision,
  VisibilityStatus,
} from '@/redux/slices/studentDecisionWebsocketSlice'
import { Badge } from '../ui/badge'
import { PaperAirplaneIcon } from '@heroicons/react/24/outline'
import { Checkbox } from '../ui/Checkbox'
import ConfirmationModal from '../OGE/OGEDeletionModal'
import { useResearchAdjudicationDrafts } from '@/hooks/useResearchAdjudicationDrafts'
import { ResearchOptionsStatus } from '@/redux/slices/researchModeSlice'
import { Sparkles, Loader2 } from 'lucide-react'

interface SendAdjudicationModalProps {
  selectedDecision: StudentDecision | null
  studentDecisions: StudentDecision[]
  onClose: () => void
  onSend: (message: string, decisionId: string) => void
  onSave: (
    decisionId: string,
    approvalStatus: VisibilityStatus,
    formattedDecision?: string
  ) => void
  roomId: string
  isResearchMode: boolean
}

export function SendAdjudicationModal({
  selectedDecision,
  studentDecisions,
  onClose,
  onSend,
  onSave,
  roomId,
  isResearchMode,
}: SendAdjudicationModalProps) {
  const [message, setMessage] = useState('')
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false)
  const [activeDecisionId, setActiveDecisionId] = useState<string | null>(null)
  const [isPublicApproved, setIsPublicApproved] = useState<VisibilityStatus>(
    VisibilityStatus.PENDING_APPROVAL
  )

  const [modifiedDecision, setModifiedDecision] = useState('')
  const [showOriginal, setShowOriginal] = useState(true)
  const [saveNotification, setSaveNotification] = useState(false)
  const { generateDraftsForDecisionIds, isGeneratingDrafts } =
    useResearchAdjudicationDrafts(roomId)

  const activeDecision = useMemo(
    () =>
      studentDecisions.find(
        (decision) => decision.decision_id === activeDecisionId
      ) || selectedDecision,
    [studentDecisions, activeDecisionId, selectedDecision]
  )

  const adjudicationAdded = !!activeDecision?.adjudication

  const unadjudicatedDecisions = useMemo(
    () => studentDecisions.filter((decision) => !decision.adjudication),
    [studentDecisions]
  )

  useEffect(() => {
    if (selectedDecision) {
      setActiveDecisionId(selectedDecision.decision_id)
      setMessage(selectedDecision?.adjudication || '')
      setModifiedDecision(selectedDecision?.formatted_decision || '')
      setIsPublicApproved(selectedDecision?.visibility_status!)
    }
  }, [selectedDecision])

  useEffect(() => {
    if (activeDecision) {
      setMessage(activeDecision.adjudication || '')
      setModifiedDecision(activeDecision.formatted_decision || '')
      setIsPublicApproved(
        activeDecision.visibility_status || VisibilityStatus.PENDING_APPROVAL
      )
    }
  }, [activeDecision])

  const handleSend = useCallback(() => {
    if (activeDecisionId) {
      onSend(message, activeDecisionId)
      setMessage('')
    }
  }, [activeDecisionId, message, onSend])

  const handleSave = useCallback(() => {
    if (activeDecisionId) {
      onSave(activeDecisionId, isPublicApproved, modifiedDecision)
      setSaveNotification(true)
      setTimeout(() => setSaveNotification(false), 3000)
    }
  }, [activeDecisionId, isPublicApproved, modifiedDecision, onSave])

  const handleDecisionSelect = useCallback((decision: StudentDecision) => {
    setActiveDecisionId(decision.decision_id)
  }, [])

  const handleGenerateAdjudication = useCallback(async () => {
    if (!activeDecisionId) return
    const drafts = await generateDraftsForDecisionIds(
      [activeDecisionId],
      'Adjudication draft generated'
    )
    const generated = drafts.find(
      (draft) => String(draft.student_decision_id) === String(activeDecisionId)
    )
    if (!generated || generated.status !== ResearchOptionsStatus.READY) return
    setMessage(generated.suggested_adjudication || '')
  }, [activeDecisionId, generateDraftsForDecisionIds])

  const isFormValid = useMemo(() => message.trim() !== '', [message])

  const handleClose = useCallback(() => {
    setMessage('')
    setActiveDecisionId(null)
    setIsPublicApproved(VisibilityStatus.PENDING_APPROVAL)
    setModifiedDecision('')
    setShowOriginal(true)
    onClose()
  }, [onClose])

  return (
    <Dialog open={!!selectedDecision} onOpenChange={onClose}>
      <DialogContent className='sm:max-w-[900px] max-h-[85vh] overflow-y-auto'>
        <DialogHeader>
          <div className='flex items-center gap-2'>
            <div className='p-3 bg-secondary rounded-lg'>
              <img src='/icons/adjudicate.svg' alt='' className='w-5 h-5' />
            </div>
            <div>
              <DialogTitle className='text-base text-[#0F172A] font-bold'>
                Adjudicate Decision
              </DialogTitle>
              <DialogDescription className='text-sm text-[#64748B]'>
                Adjudicate decisions from any round.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {saveNotification && (
          <div className='p-3 bg-green-50 border border-green-200 rounded-lg'>
            <div className='flex items-center gap-2'>
              <div className='w-2 h-2 bg-green-500 rounded-full'></div>
              <span className='text-sm font-medium text-green-800'>
                Changes saved successfully!
              </span>
            </div>
          </div>
        )}

        <div className='space-y-6'>
          {studentDecisions.length > 1 && (
            <div className='space-y-3'>
              <Label className='text-sm text-black font-bold'>
                Select Decision to Adjudicate
              </Label>
              <div className='flex flex-wrap gap-2'>
                {studentDecisions.map((decision) => {
                  const isActive = activeDecisionId === decision.decision_id
                  const hasAdjudication = !!decision.adjudication

                  return (
                    <Button
                      key={decision.decision_id}
                      variant={isActive ? 'default' : 'outline'}
                      size='sm'
                      onClick={() => handleDecisionSelect(decision)}
                      className={`flex items-center gap-2 ${
                        isActive
                          ? 'bg-dark-blue hover:bg-dark-blue-hovered'
                          : hasAdjudication
                            ? 'border-green-300 bg-green-50 hover:bg-green-100 text-green-700'
                            : 'border-orange-300 hover:border-orange-400'
                      }`}
                    >
                      <span>Round {decision.round_number}</span>
                    </Button>
                  )
                })}
              </div>
            </div>
          )}

          {activeDecision && (
            <div className='space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200'>
              <div className='space-y-2'>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <Label className='text-sm text-black font-bold'>
                      Decision - Round {activeDecision.round_number}
                    </Label>
                    {activeDecision.late_submission && (
                      <Badge variant='destructive' className='text-xs'>
                        Late
                      </Badge>
                    )}
                  </div>
                  {activeDecision.is_public && (
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => setShowOriginal(!showOriginal)}
                      className='text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-100'
                    >
                      {showOriginal ? 'Show Modified' : 'Show Original'}
                    </Button>
                  )}
                </div>

                {showOriginal && (
                  <div className='p-3 bg-white rounded-md border'>
                    <p className='whitespace-pre-wrap text-sm text-gray-700'>
                      {activeDecision.decision || 'No decision provided'}
                    </p>
                  </div>
                )}

                {activeDecision.decision_justification && (
                  <div className='space-y-1'>
                    <Label className='text-xs text-gray-500 font-semibold uppercase tracking-wide'>
                      Justification
                    </Label>
                    <div className='p-3 bg-white rounded-md border'>
                      <p className='whitespace-pre-wrap text-sm text-gray-700'>
                        {activeDecision.decision_justification}
                      </p>
                    </div>
                  </div>
                )}

                {!showOriginal && (
                  <div className='space-y-2'>
                    <Label className='text-xs text-gray-600'>
                      Modified Version
                    </Label>
                    <Textarea
                      value={modifiedDecision}
                      onChange={(e) => setModifiedDecision(e.target.value)}
                      placeholder='Rewrite or format the decision here...'
                      className='min-h-[120px] resize-none bg-white'
                    />
                  </div>
                )}
              </div>

              {activeDecision.is_public && (
                <>
                  <div className='flex items-start space-x-3 p-3 bg-white rounded-md border border-gray-200'>
                    <Checkbox
                      id={`public-approval-${activeDecisionId}`}
                      checked={
                        isPublicApproved === VisibilityStatus.APPROVED_PUBLIC
                      }
                      onChange={(e) =>
                        setIsPublicApproved(
                          e.target.checked
                            ? VisibilityStatus.APPROVED_PUBLIC
                            : VisibilityStatus.PENDING_APPROVAL
                        )
                      }
                      className='mt-0.5'
                    />
                    <div className='space-y-1'>
                      <Label
                        htmlFor={`public-approval-${activeDecisionId}`}
                        className='text-sm font-medium text-gray-900 cursor-pointer'
                      >
                        Approve for Public Sharing
                      </Label>
                      <p className='text-xs text-gray-500'>
                        This decision can be shared with other students.
                      </p>
                    </div>
                  </div>
                  <div className='flex justify-end mt-3'>
                    <Button
                      onClick={handleSave}
                      disabled={!isPublicApproved}
                      className='bg-dark-blue hover:bg-dark-blue-hovered disabled:bg-[#CED2D8] text-white disabled:text-[#6D6E71]'
                    >
                      Save
                    </Button>
                  </div>
                </>
              )}

              <div className='space-y-2'>
                <div className='flex items-center justify-between'>
                  <Label className='text-sm text-black font-bold'>
                    Adjudication
                  </Label>
                  {isResearchMode && !adjudicationAdded && (
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={() => void handleGenerateAdjudication()}
                      disabled={isGeneratingDrafts}
                    >
                      {isGeneratingDrafts ? (
                        <Loader2 className='w-4 h-4 animate-spin' />
                      ) : (
                        <>
                          <Sparkles className='w-4 h-4 mr-1' />
                          Generate adjudication
                        </>
                      )}
                    </Button>
                  )}
                </div>
                <Textarea
                  value={message}
                  readOnly={adjudicationAdded}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={
                    adjudicationAdded
                      ? 'This decision has already been adjudicated'
                      : 'Type your adjudication here'
                  }
                  className={`min-h-[100px] resize-none ${
                    adjudicationAdded
                      ? 'bg-gray-100 text-gray-600'
                      : 'bg-[#FFFFFF] placeholder:text-[#94A3B8]'
                  } placeholder:text-sm`}
                />
              </div>
            </div>
          )}

          {unadjudicatedDecisions.length > 1 && (
            <div className='p-3 bg-amber-50 border border-amber-200 rounded-lg'>
              <div className='flex items-center gap-2 mb-2'>
                <PaperAirplaneIcon className='w-4 h-4 text-amber-600' />
                <span className='text-sm font-medium text-amber-800'>
                  Pending Adjudications
                </span>
              </div>
              <p className='text-xs text-amber-700'>
                {unadjudicatedDecisions.length} decision
                {unadjudicatedDecisions.length > 1 ? 's' : ''} still need
                {unadjudicatedDecisions.length === 1 ? 's' : ''} adjudication.
              </p>
            </div>
          )}

          <div className='flex justify-end gap-3'>
            <Button
              onClick={handleClose}
              variant={'outline'}
              className='text-dark-blue'
            >
              Cancel
            </Button>
            <Button
              onClick={() => setIsConfirmationModalOpen(true)}
              disabled={!isFormValid || adjudicationAdded}
              className='bg-dark-blue hover:bg-dark-blue-hovered disabled:bg-[#CED2D8] text-white disabled:text-[#6D6E71]'
            >
              Send
            </Button>
          </div>
        </div>
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          onClose={() => setIsConfirmationModalOpen(false)}
          onConfirm={handleSend}
          title='Send Adjudication'
          description={`Are you sure you want to send adjudication to ${selectedDecision?.user_name}?`}
          confirmButtonText='Send'
          cancelButtonText='Cancel'
        />
      </DialogContent>
    </Dialog>
  )
}
